// drmHelpers.js
'use strict';

import {
  COM_APPLE_FPS,
  COM_MICROSOFT_PLAYREADY,
  COM_MICROSOFT_PLAYREADY_HARDWARE,
  COM_MICROSOFT_PLAYREADY_RECOMMENDATION,
  COM_MICROSOFT_PLAYREADY_RECOMMENDATION_3000,
  COM_WIDEVINE_ALPHA,
  ORG_W3_CLEARKEY,
} from '../resource/drmConstants.js';
import { AUDIO_CONTENT_TYPE } from '../resource/playerUtils.js';
import getResourceUrl from '../resource/getResourceUrl.js';
import { getHeaders } from '../resource/fetchOptionHeader.js';
import { videoContentType } from '../resource/getDrmInfo.js';
import { PlayerEngine } from '../selector/getCurrentEngine.js';
import { player as playerDebug } from '../resource/debug.js';

const drmDetectLog = playerDebug.extend('log:drm-detect');

const PLAYREADY_KEY_SYSTEMS = new Set([
  COM_MICROSOFT_PLAYREADY,
  COM_MICROSOFT_PLAYREADY_HARDWARE,
  COM_MICROSOFT_PLAYREADY_RECOMMENDATION,
  COM_MICROSOFT_PLAYREADY_RECOMMENDATION_3000,
]);

const getLicenseKeySystem = keySystem =>
  PLAYREADY_KEY_SYSTEMS.has(keySystem) ? COM_MICROSOFT_PLAYREADY : keySystem;

export const buildGenericDrmConfig = (
  keySystem,
  { drmToken, robustness } = {}
) => {
  const isFairPlay = keySystem === COM_APPLE_FPS;
  return {
    keySystem,
    licenseServerUrl:
      getResourceUrl({
        endpoint: `/drm/authorize/${getLicenseKeySystem(keySystem)}`,
      })?.href || null,
    certificateUrl: isFairPlay
      ? getResourceUrl({ endpoint: '/drm/certs/com.apple.fps.cer' })?.href ||
        null
      : null,
    baseHeaders: getHeaders(),
    drmToken,
    robustness,
    requiresKid: keySystem !== ORG_W3_CLEARKEY,
  };
};

/** @returns {Promise<'supported'|'unsupported'>} */
export const checkDrmSupportWithNavigator = async ({
  keySystem,
  robustness,
}) => {
  if (keySystem === COM_APPLE_FPS) {
    const isSupported = window?.WebKitMediaKeys?.isTypeSupported(
      robustness,
      'video/mp4'
    );
    return isSupported ? 'supported' : 'unsupported';
  }

  if (keySystem === ORG_W3_CLEARKEY) {
    return robustness === 'SW_SECURE_CRYPTO' ? 'supported' : 'unsupported';
  }

  if (typeof navigator?.requestMediaKeySystemAccess !== 'function') {
    return 'unsupported';
  }

  const config = [
    {
      initDataTypes: ['cenc', 'webm'],
      videoCapabilities: [
        {
          contentType: videoContentType,
          robustness: robustness || '',
        },
      ],
      audioCapabilities: [
        {
          contentType: AUDIO_CONTENT_TYPE,
          robustness:
            keySystem === COM_WIDEVINE_ALPHA
              ? 'SW_SECURE_CRYPTO'
              : robustness || '',
        },
      ],
    },
  ];

  try {
    // Only query MediaKeySystemAccess, do not create MediaKeys
    await navigator.requestMediaKeySystemAccess(keySystem, config);
    return 'supported';
  } catch {
    return 'unsupported';
  }
};

/**
 * Run a single DRM test using shaka-player.
 * @param {object} params
 * @param {string} params.keySystem - DRM key system to test.
 * @param {string} params.robustness - DRM robustness level.
 * @param {string} params.assetUrl - Test manifest URL.
 * @param {string} params.token - Auth token for license requests.
 * @param {string} params.clientId - Client id for license requests.
 * @param {string} params.widevineServerCertificateBase64 - Widevine certificate.
 * @param {number} params.timeoutSeconds - Test timeout.
 * @param {string} params.debugNo - Debug label.
 * @returns {Promise<string>} 'supported' | 'unsupported' | 'timeout'.
 */
export const checkDrmSupportWithShaka = async ({
  keySystem,
  robustness,
  assetUrl,
  token,
  clientId,
  widevineServerCertificateBase64,
  timeoutSeconds,
  debugNo,
}) => {
  const shakaFactory = await import('../resource/shakaFactory.js');
  let player;
  try {
    player = await shakaFactory.create({ debugNo });
    if (player.error) return 'unsupported';

    player.widevineServerCertificateBase64 = widevineServerCertificateBase64;
    player.token = token;
    // Request filter reads this for the X-Client-ID license header.
    player.clientId = clientId;
    shakaFactory.updateShakaPlayerConfiguration({
      player,
      keySystem,
      robustness,
    });
    shakaFactory.registerShakaPlayerDrmRequestFilter(player);

    const videoElement = player.getMediaElement();
    videoElement.muted = true;
    videoElement.setAttribute('playsinline', '');
    videoElement.style.cssText =
      'position:fixed;top:-9999px;left:-9999px;width:1px;height:1px;overflow:hidden;';
    document.body.appendChild(videoElement);

    const loadTimedOut = await Promise.race([
      player.load(assetUrl).then(() => false),
      new Promise(resolve =>
        setTimeout(() => resolve(true), timeoutSeconds * 1000)
      ),
    ]);

    if (loadTimedOut) return 'timeout';

    return await new Promise(resolve => {
      const timeoutId = setTimeout(
        () => resolve('timeout'),
        timeoutSeconds * 1000
      );

      videoElement.addEventListener(
        'timeupdate',
        () => {
          clearTimeout(timeoutId);
          resolve('supported');
        },
        { once: true }
      );

      player.addEventListener('error', event => {
        const shakaError = event?.detail;
        drmDetectLog('shaka error event', {
          code: shakaError?.code,
          category: shakaError?.category,
          data: shakaError?.data,
        });
        clearTimeout(timeoutId);
        resolve('unsupported');
      });

      player.play().catch(error => {
        drmDetectLog('play() rejected', {
          name: error?.name,
          message: error?.message,
        });
        clearTimeout(timeoutId);
        resolve('unsupported');
      });
    });
  } catch (error) {
    drmDetectLog('load failed', {
      code: error?.code,
      category: error?.category,
      data: error?.data,
      message: error?.message,
    });
    return 'unsupported';
  } finally {
    if (player && !player.error) {
      try {
        await shakaFactory.destroy({
          debugNo,
          player,
          shouldRecycleVideoElement: false,
        });
      } catch {
        /* ignore cleanup errors */
      }
    }
  }
};

/**
 * @param {object} params
 * @param {object} params.player - clpp.Player instance (already loaded via pool).
 * @param {HTMLVideoElement} [params.videoElement] - Video element from the pool.
 * @param {string} params.robustness - DRM robustness level.
 * @param {number} params.timeoutSeconds - Test timeout.
 * @param {AbortSignal} params.abortSignal - Abort signal.
 * @returns {Promise<'supported'|'unsupported'|'timeout'|'aborted'>}
 */
export const checkDrmSupportWithPRESTOplay = async ({
  player,
  videoElement,
  robustness,
  timeoutSeconds = 20,
  abortSignal = null,
}) => {
  const startTime = performance.now();
  const robustnessLabel = (robustness || '')
    .replace('HW_SECURE_', 'HW_')
    .replace('SW_SECURE_', 'SW_');

  if (!player) {
    return 'unsupported';
  }

  if (abortSignal?.aborted) {
    return 'aborted';
  }

  const video = videoElement || player.getMedia?.();
  if (!video) {
    return 'unsupported';
  }

  return new Promise(resolve => {
    let timeoutId = null;
    let isDone = false;

    const complete = result => {
      if (isDone) return;
      isDone = true;
      clearTimeout(timeoutId);

      if (abortSignal) {
        abortSignal.removeEventListener('abort', handleAbort);
      }

      drmDetectLog(
        `${robustnessLabel} → ${result} (${(performance.now() - startTime).toFixed(0)}ms)`
      );

      try {
        video.removeEventListener('timeupdate', handleSuccess);
        player.removeEventListener('error', handleError);
      } catch {
        /* ignore cleanup errors */
      }

      resolve(result);
    };

    const handleSuccess = () => complete('supported');
    const handleError = () => complete('unsupported');
    const handleTimeout = () => complete('timeout');
    const handleAbort = () => complete('aborted');

    try {
      timeoutId = setTimeout(handleTimeout, timeoutSeconds * 1000);

      if (abortSignal) {
        abortSignal.addEventListener('abort', handleAbort);
      }

      video.addEventListener('timeupdate', handleSuccess, { once: true });
      player.addEventListener('error', handleError);

      player.play().catch(() => complete('unsupported'));
    } catch {
      complete('unsupported');
    }
  });
};

export const DrmDetectors = {
  navigator: { checkSupport: checkDrmSupportWithNavigator },
  [PlayerEngine.SHAKA]: { checkSupport: checkDrmSupportWithShaka },
  [PlayerEngine.PRESTOPLAY]: { checkSupport: checkDrmSupportWithPRESTOplay },
};

/** Build DRM license request headers */
export const buildLicenseHeaders = ({ kid, token, clientId } = {}) => ({
  ...(kid && { 'X-DRM-KID': kid }),
  ...(token && { Authorization: `Bearer ${token}` }),
  ...(clientId && { 'X-Client-ID': clientId }),
});
